import React from 'react';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
// The `campaigns` namespace is registered at boot (`src/i18n.ts`), so this adds
// no copy to the initial bundle.
import { NS } from '../../i18n';
import type { CampaignListRow } from '../../services/campaignsClient';
import { Bone, SkeletonStatus, TextBone } from '../CampaignSkeleton/CampaignSkeleton';
import '../CampaignsToolbar/CampaignsToolbar.css';
import './CampaignsTable.css';

/*
 * The campaigns list while it loads. ONE definition, drawn by the list itself
 * (`CampaignsTable`, while the query is in flight) and by the route skeleton
 * (`CampaignRouteSkeleton`, before the page's chunk arrives): same header, same
 * column widths, same bone rows — so a refresh goes from one to the other
 * without a pixel moving.
 *
 * No hooks beyond the translation and no page modules, on purpose: the route
 * skeleton is in the initial bundle.
 */

export type ListColumnKey =
  | 'campaign'
  | 'status'
  | 'progress'
  | 'contactability'
  | 'outcome'
  | 'pace'
  | 'actions';

export interface ListColumnLayout {
  key: ListColumnKey;
  /** The header's copy, in the `campaigns` namespace. The row menu has none. */
  titleKey?: string;
  width?: number;
  align?: 'right';
}

/** The list's columns as laid out — fixed widths; the campaign column takes what is left. */
export const LIST_COLUMNS: readonly ListColumnLayout[] = [
  { key: 'campaign', titleKey: 'columns.campaign' },
  { key: 'status', titleKey: 'columns.status', width: 120 },
  { key: 'progress', titleKey: 'columns.progress', width: 150 },
  { key: 'contactability', titleKey: 'columns.contactability', width: 150 },
  { key: 'outcome', titleKey: 'columns.outcome', width: 160 },
  { key: 'pace', titleKey: 'columns.pace', width: 175 },
  { key: 'actions', width: 76, align: 'right' },
];

/** Rows drawn while the list loads: about a laptop screen's worth. */
const SKELETON_ROWS: CampaignListRow[] = Array.from(
  { length: 5 },
  (_, i) => ({ id: `skeleton-${i}` }) as CampaignListRow,
);

/** Two stacked figures (`DualMetric`): 14/22 over 12/20. */
const DualBone: React.FC<{ top: string; bottom: string }> = ({ top, bottom }) => (
  <div className="campaign-skeleton__stack campaign-skeleton__stack--tight">
    <TextBone width={top} />
    <TextBone width={bottom} size={12} lineHeight={20} />
  </div>
);

/**
 * Each column's loading cell, the shape of what it will hold
 * (`CampaignColumns.tsx`), so the rows keep their height when the data lands.
 */
const SKELETON_CELL: Record<ListColumnKey, React.ReactNode> = {
  campaign: (
    <div className="campaign-skeleton__stack campaign-skeleton__stack--tight">
      <TextBone width="55%" />
      <div className="campaign-skeleton__row">
        <Bone width={88} height={24} shape="pill" />
        <TextBone width={96} size={12} lineHeight={20} />
      </div>
    </div>
  ),
  status: <Bone width={68} height={22} shape="pill" />,
  progress: (
    <div className="campaign-skeleton__stack campaign-skeleton__stack--tight">
      <TextBone width="60%" size={12} lineHeight={20} />
      <Bone height={4} shape="pill" />
    </div>
  ),
  contactability: <DualBone top="40%" bottom="80%" />,
  outcome: <DualBone top="30%" bottom="70%" />,
  pace: <DualBone top="45%" bottom="75%" />,
  actions: <Bone width={32} height={32} className="campaigns-table__skeleton-action" />,
};

/**
 * The table with its real header and bone rows (the selection column too). The
 * status carries the translated label, visually hidden.
 */
export const CampaignsTableSkeleton: React.FC = () => {
  const { t } = useTranslation(NS);
  return (
    <SkeletonStatus label={t('loading.list')} testId="campaigns-table-skeleton">
      <Table<CampaignListRow>
        dataSource={SKELETON_ROWS}
        columns={LIST_COLUMNS.map((column) => ({
          key: column.key,
          title: column.titleKey ? t(column.titleKey) : undefined,
          width: column.width,
          align: column.align,
          render: () => SKELETON_CELL[column.key],
        }))}
        rowKey="id"
        pagination={false}
        // Wide columns scroll inside the table, never the page (768 and 480).
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys: [],
          renderCell: () => <Bone width={16} height={16} />,
          columnTitle: <Bone width={16} height={16} />,
        }}
      />
    </SkeletonStatus>
  );
};

/**
 * The toolbar's place before the page mounts it (`CampaignsToolbar`): the
 * search, the two selects and the count, in the toolbar's own classes so they
 * take its widths and its 768 / 480 rules. Only the route skeleton draws this —
 * once the page is there its real (disabled) toolbar stands in.
 */
export const CampaignsToolbarSkeleton: React.FC = () => (
  <div className="campaigns-toolbar" aria-hidden="true">
    <span className="campaigns-toolbar__search">
      <Bone height="var(--silia-ctl-height)" />
    </span>
    <span className="campaigns-toolbar__select">
      <Bone height="var(--silia-ctl-height)" />
    </span>
    <span className="campaigns-toolbar__select">
      <Bone height="var(--silia-ctl-height)" />
    </span>
    <span className="campaigns-toolbar__count">
      <TextBone width={64} size={12} lineHeight={20} />
    </span>
  </div>
);
